import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Grid,
  Divider,
  Alert,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { getCompanyLogs } from './api/underwritingApi';

const DetailItem = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: '500' }}>
      {value}
    </Typography>
  </Grid>
);

const LogDetails = ({ log }) => {
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  switch (log.eventType) {
    case 'Initial Request':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedLimit)} />
          <DetailItem label="ARC Score" value={log.arcScore} />
        </Grid>
      );
    case 'Requested Limit':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Previous Limit" value={formatCurrency(log.previousLimit)} />
          <DetailItem label="New Limit" value={formatCurrency(log.newLimit)} />
        </Grid>
      );
    case 'Recommended Limit':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Previous Limit" value={formatCurrency(log.previousLimit)} />
          <DetailItem label="New Limit" value={formatCurrency(log.newLimit)} />
        </Grid>
      );
    case 'ARC Score':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Previous Score" value={log.previousScore} />
          <DetailItem label="New Score" value={log.newScore} />
        </Grid>
      );
    case 'Rationale':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Previous Rationale" value={log.previousRationale} />
          <DetailItem label="New Rationale" value={log.newRationale} />
        </Grid>
      );
    case 'Underwriting':
      if(log.action === 'Underwriting started') {
        return (
          <Grid container spacing={3}>
            <DetailItem label="Message" value={`Underwriting started for the company`} />
          </Grid>
        );
      }
      return (
        <Grid container spacing={3}>
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedLimit)} />
          <DetailItem label="Recommended Limit" value={formatCurrency(log.recommendedLimit)} />
          <DetailItem label="ARC Score" value={log.arcScore} />
          <DetailItem label="Rationale" value={log.reason} />
        </Grid>
      );
    case 'Manual Evaluation':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Recommended Limit" value={formatCurrency(log.recommendedLimit)} />
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedAmount)} />
          <DetailItem label="Reason" value={log.comment} />

        </Grid>
      );
    case 'Risk Mitigation':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedLimit)} />
          <DetailItem label="Reason" value={log.comment} />
          <DetailItem label="Mitigation Type" value={log.mitigationType} />
        </Grid>
      );
    case 'Approval':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Recommended Limit" value={formatCurrency(log.recommendedLimit)} />
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedLimit)} />
          <DetailItem label="Approved Limit" value={formatCurrency(log.approvedLimit)} />
          <DetailItem label="ARC Score" value={log.arcScore} />
        </Grid>
      );
    case 'Rejection':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Reason" value={log.reason} />
          <DetailItem label="Requested Limit" value={formatCurrency(log.requestedLimit)} />
          <DetailItem label="ARC Score" value={log.arcScore} />
        </Grid>
      );
    case 'Status Change':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Previous Status" value={log.previousStatus} />
          <DetailItem label="New Status" value={log.newStatus} />
          <DetailItem label="Reason" value={log.reason} />
        </Grid>
      );
    case 'Document Upload':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Document Type" value={log.documentType} />
          <DetailItem label="File Name" value={log.fileName} />
        </Grid>
      );
    case 'Approval Request':
      return (
        <Grid container spacing={3}>
          <DetailItem label="Role Name" value={log.roleName} />
          <DetailItem label="Name" value={log.name} />
        </Grid>
      );
    default:
      return null;
  }
};

const Logs = ({ tenantId, companyId, companyName }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState({});

  useEffect(() => {
    const fetchLogs = async () => {
      if (tenantId && companyId && companyName) {
        try {
          setLoading(true);
          const response = await getCompanyLogs(companyId, { page: 1, limit: 50 });
          if (response.length > 0) {
            const sortedLogs = response.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setLogs(sortedLogs);
          } else {
            setLogs([]);
          }
          setError(null);
        } catch (err) {
          setError('Failed to fetch logs.');
          console.error('Error fetching logs:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchLogs();
  }, [tenantId, companyId, companyName]);

  const toggleExpanded = (logId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const formatDate = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(new Date(timestamp));
  };

  const getEventIcon = (eventType) => {
    const iconProps = { sx: { width: 20, height: 20 } };
    
    switch (eventType) {
      case 'Approval':
        return <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4caf50' }} />;
      case 'Requested Limit':
        return <AttachMoneyIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      case 'Recommended Limit':
        return <AttachMoneyIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      case 'ARC Score':
        return <SecurityIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
      case 'Rationale':
        return <DescriptionIcon {...iconProps} sx={{ ...iconProps.sx, color: '#009688' }} />;
      case 'Underwriting':
        return <DescriptionIcon {...iconProps} sx={{ ...iconProps.sx, color: '#009688' }} />;
      case 'Manual Evaluation':
      case 'Risk Mitigation':
        return <WarningIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f44336' }} />;
      case 'Approval Request':
        return <EmailIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      case 'Initial Request':
      default:
        return <AccessTimeIcon {...iconProps} sx={{ ...iconProps.sx, color: '#757575' }} />;
    }
  };

  const getEventTypeLabel = (eventType) => {
    return eventType;
  };

  const getChipColor = (eventType) => {
    const colors = {
      'Approval': 'success',
      'Rejection': 'error',
      'Requested Limit': 'primary',
      'Recommended Limit': 'primary',
      'ARC Score': 'warning',
      'Rationale': 'info',
      'Underwriting': 'info',
      'Manual Evaluation': 'warning',
      'Risk Mitigation': 'error',
      'Initial Request': 'default',
      'Status Change': 'primary',
      'Document Upload': 'info',
      'Approval Request': 'info',
    };
    return colors[eventType] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: '600', color: '#1A2A56', mb: 1 }}>
          Approval Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete timeline of approval-related events and changes
        </Typography>
      </Box>

      {logs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <AccessTimeIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No logs available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Events will appear here as they occur for this company.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {/* Timeline */}
          <Box sx={{ position: 'relative' }}>
            {logs.map((log, index) => (
              <Box key={log.id || index} sx={{ position: 'relative', mb: 3 }}>
                {/* Timeline line */}
                {index < logs.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '24px',
                      top: '48px',
                      width: '2px',
                      height: 'calc(100% - 48px)',
                      bgcolor: '#e0e0e0',
                      zIndex: 0
                    }}
                  />
                )}
                
                {/* Log entry */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Icon */}
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: '#f5f5f5',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1
                    }}
                  >
                    {getEventIcon(log.eventType)}
                  </Avatar>
                  
                  {/* Content */}
                  <Card sx={{ flex: 1, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <CardContent sx={{ p: 3 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Chip
                            label={getEventTypeLabel(log.eventType)}
                            color={getChipColor(log.eventType)}
                            size="small"
                            variant="filled"
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            <CalendarTodayIcon sx={{ width: 16, height: 16 }} />
                            <Typography variant="caption">
                              {formatDate(log.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Expand button */}
                        <IconButton
                          onClick={() => toggleExpanded(log.id)}
                          size="small"
                          sx={{
                            transition: 'transform 0.2s',
                            transform: expandedEntries[log.id] ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      </Box>
                      
                      {/* Main action */}
                      <Typography variant="body1" sx={{ fontWeight: '500', color: '#1a1a1a', mb: 1 }}>
                        {log.action}
                      </Typography>
                      
                      {/* User info if available */}
                      {log.username && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <PersonIcon sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {log.username}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Expanded details */}
                      <Collapse in={expandedEntries[log.id]} timeout="auto" unmountOnExit>
                        <Divider sx={{ my: 2 }} />
                        <LogDetails log={log} />
                      </Collapse>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ))}
          </Box>
          
          {/* Footer note */}
          <Alert 
            severity="info" 
            icon={<SecurityIcon />}
            sx={{ mt: 4, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Immutable Log Records
            </Typography>
            <Typography variant="body2">
              All log entries are immutable and reflect historical states accurately. 
              Changes to company data will not affect previously recorded log entries.
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Logs;
